import React, { useState, useEffect } from 'react';
import CardMCFilter from './CardMCFilter';
import { Button, Icon } from 'semantic-ui-react';
import * as XLSX from 'xlsx';
import CardMCVegaChart from './components/CardMCVegaChart';
import { VisualizationSpec } from 'vega-embed';

type CardMCSummaryOverviewProps = {
  controller: any;
  runSequence: any;
  MeChartSequence: any;
}

const thaiMonths = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
];

const CardMCSummaryOverview = (props: CardMCSummaryOverviewProps) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(1);
  const [endMonth, setEndMonth] = useState(12);

  const [viewMode, setViewMode] = useState<'overview' | '3year'>('overview');
  const [chartType, setChartType] = useState<'bar' | 'line'>('bar');

  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (viewMode === '3year') {
      setChartType('line');
    }
  }, [viewMode]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const startDateStr = viewMode === 'overview'
        ? `${year}-${String(month).padStart(2, '0')}-01 00:00:00`
        : `${year - 2}-01-01 00:00:00`;
      const lastDay = new Date(year, endMonth, 0).getDate();
      const endDateStr = viewMode === 'overview'
        ? `${year}-${String(endMonth).padStart(2, '0')}-${lastDay} 23:59:59`
        : `${year}-12-31 23:59:59`;

      if (props.MeChartSequence?.sequenceIndex === "Action") {
        const res = await props.runSequence({
          sequence: "MeChart",
          action: "QuerySQL",
          chart: viewMode === 'overview' ? "summary-overview" : "summary-overview-3year",
          startDate: startDateStr,
          endDate: endDateStr,
        });

        if (Array.isArray(res) && res?.length > 0 && res?.[0]?.items && Array.isArray(res?.[0]?.items)) {
          if (viewMode === 'overview') {
            // Pad to all 12 months — missing months get total_sales "0"
            const salesByMonth: Record<string, string> = {};
            res[0].items.forEach((d: any) => {
              salesByMonth[String(d.month)] = d.total_sales;
            });
            const padded = Array.from({ length: 12 }, (_, i) => ({
              month: String(i + 1),
              total_sales: salesByMonth[String(i + 1)] ?? "0",
            }));
            setChartData(padded);
          } else {
            setChartData(res[0].items);
          }
        } else {
          setChartData([]);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const onPrint = () => { window.print(); };

  const onDownload = () => {
    if (!chartData || chartData.length === 0) {
      alert('No data to download');
      return;
    }
    let dataToExport: any[];
    if (viewMode === 'overview') {
      dataToExport = chartData.map((d: any) => ({
        Month: thaiMonths[parseInt(d.month) - 1],
        Revenue: d.total_sales,
      }));
    } else {
      dataToExport = chartData.map((d: any) => ({
        Year: d.year,
        Month: thaiMonths[parseInt(d.month) - 1],
        Revenue: d.total_sales,
      }));
    }
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SummaryOverview");
    XLSX.writeFile(wb, `Summary_Overview_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // Data for Vega — derived from chartData

  const getVegaOverviewData = () => {
    return chartData.map((d: any) => ({
      month: thaiMonths[parseInt(d.month) - 1],
      value: parseInt(d.total_sales || 0),
      order: parseInt(d.month) - 1,
    }));
  };

  const getVegaThreeYearData = () => {
    return chartData.map((d: any) => ({
      month: thaiMonths[parseInt(d.month) - 1],
      year: d.year,
      value: parseInt(d.total_sales || 0),
      order: parseInt(d.month) - 1,
    }));
  };

  // --- Vega Specs ---

  const getSpec = (): VisualizationSpec => {
    if (viewMode === 'overview') {
      const data = getVegaOverviewData();
      if (chartType === 'bar') {
        return {
          width: "container",
          height: "container",
          autosize: { type: "fit", contains: "padding" },
          data: { values: data },
          mark: { type: "bar", tooltip: true },
          encoding: {
            x: { field: "month", type: "ordinal", axis: { title: "เดือน (Month)", labelAngle: -45 }, sort: { field: "order" } },
            y: { field: "value", type: "quantitative", axis: { title: "Revenue (THB)" } },
            color: { value: "#4285F4" },
            tooltip: [
              { field: "month", title: "Month" },
              { field: "value", title: "Revenue", format: ",.2f" }
            ]
          }
        };
      } else {
        return {
          width: "container",
          height: "container",
          autosize: { type: "fit", contains: "padding" },
          data: { values: data },
          mark: { type: "line", point: true, tooltip: true },
          encoding: {
            x: { field: "month", type: "ordinal", axis: { title: "เดือน (Month)", labelAngle: -45 }, sort: { field: "order" } },
            y: { field: "value", type: "quantitative", axis: { title: "Revenue (THB)" } },
            color: { value: "#4285F4" },
            tooltip: [
              { field: "month", title: "Month" },
              { field: "value", title: "Revenue", format: ",.2f" }
            ]
          }
        };
      }
    } else {
      // 3 Year Data (Line Chart)
      return {
        width: "container",
        height: "container",
        autosize: { type: "fit", contains: "padding" },
        data: { values: getVegaThreeYearData() },
        mark: { type: "line", point: true, tooltip: true },
        encoding: {
          x: { field: "month", type: "ordinal", axis: { title: "เดือน (Month)", labelAngle: -45 }, sort: { field: "order" } },
          y: { field: "value", type: "quantitative", axis: { title: "Revenue (THB)" } },
          color: {
            field: "year",
            type: "nominal",
            scale: { range: ['#4285F4', '#EA4335', '#FDBB2D'] },
            legend: { title: "Year" }
          },
          tooltip: [
            { field: "month", title: "Month" },
            { field: "year", title: "Year" },
            { field: "value", title: "Revenue", format: ",.2f" }
          ]
        }
      };
    }
  };

  return (
    <div id="card-mc-summary-overview">
      {/* Row 1: Toggles */}
      <div className="controls-container">
        {/* Left: View Mode Toggles */}
        <Button.Group>
          <Button
            active={viewMode === 'overview'}
            style={viewMode === 'overview' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
            onClick={() => setViewMode('overview')}
          >
            สรุปยอดขายสินค้า และบริการ
          </Button>
          <Button
            active={viewMode === '3year'}
            style={viewMode === '3year' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
            onClick={() => setViewMode('3year')}
          >
            สรุปยอดขายสินค้า และบริการ 3 ปีย้อนหลัง
          </Button>
        </Button.Group>

        {/* Right: Chart Type Toggles */}
        <Button.Group>
          <Button
            active={chartType === 'bar'}
            style={chartType === 'bar' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
            onClick={() => setChartType('bar')}
            icon
            disabled={viewMode === '3year'}
          >
            <Icon name='chart bar' /> Bar
          </Button>
          <Button
            active={chartType === 'line'}
            style={chartType === 'line' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
            onClick={() => setChartType('line')}
            icon
          >
            <Icon name='chart line' /> Line
          </Button>
        </Button.Group>
      </div>

      {/* Row 2: Filter and Download */}
      <div className="filter-container">
        <CardMCFilter
          mode={viewMode === 'overview' ? 'oneYearMonth' : 'yearOnly'}
          year={year}
          month={month}
          endYear={year}
          endMonth={endMonth}
          onYearChange={setYear}
          onMonthChange={setMonth}
          onEndYearChange={() => { }}
          onEndMonthChange={setEndMonth}
          onSearch={fetchData}
          onPrint={onPrint}
        >
          <Button style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white' }} size='small' icon labelPosition='left' onClick={onDownload}>
            <Icon name='download' />
            Download .xlsx
          </Button>
          <Button style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white' }} size='small' icon labelPosition='left' disabled title="Transpose">
            <Icon name='exchange' />
            Transpose
          </Button>
        </CardMCFilter>
      </div>

      {/* Row 4: Chart */}
      <div className="chart-wrapper">
        {loading ? (
          <div>Loading...</div>
        ) : (
          <CardMCVegaChart spec={getSpec()} actions={false} />
        )}
      </div>
    </div>
  );
};

export default CardMCSummaryOverview;
